import { createStyles, Stack } from '@mantine/core';
import type { ReactNode } from 'react';
import { memo } from 'react';
import { RichEditor } from '../../../RichEditor';
import { parseAnalysisContent } from '../../utils';
import { AnalysisSection } from './AnalysisSection';
import { ReasoningCollapse } from './ReasoningCollapse';

const SECTION_HEADERS = [
	'ENTITY ANALYSIS',
	'RESOURCE MAPPING',
	'QUERY STRATEGY',
	'INSIGHT EXTRACTION',
];

const useStyles = createStyles((theme) => ({
	editor: {
		'& .ProseMirror': {
			color: theme.other.getColor('text/secondary/default'),
		},
		'& .ProseMirror > a.heading-name': {
			display: 'none',
		},
		'#thinking-section-content & .ProseMirror': {
			fontSize: theme.fontSizes.sm,
			'& br.ProseMirror-trailingBreak': {
				display: 'none',
			},
		},
		paddingLeft: theme.other.space[7],
	},
}));

function hasSectionHeaders(content: string): boolean {
	// Split content into lines and check each line
	const lines = content.split('\n');

	return lines.some((line) => {
		const trimmedLine = line.trim();
		// Check if the line is exactly one of the section headers
		// or if it's a section header followed by a colon
		return SECTION_HEADERS.some(
			(header) =>
				trimmedLine === header ||
				trimmedLine === `${header}:` ||
				trimmedLine.startsWith(`${header}: `)
		);
	});
}

export const ThoughtCollapse = memo(
	({ text, seconds }: { text: string; seconds?: number }) => {
		const { classes } = useStyles();

		let content: ReactNode = (
			<RichEditor readOnly value={text} className={classes.editor} />
		);
		if (hasSectionHeaders(text)) {
			content = (
				<Stack spacing={0}>
					{parseAnalysisContent(text).map((section, sectionIndex, sections) => (
						<AnalysisSection
							key={section.title + sectionIndex}
							section={section}
							isLast={sectionIndex === sections.length - 1}
						/>
					))}
				</Stack>
			);
		}

		return (
			<ReasoningCollapse
				title={`Thought ${seconds ? `for ${seconds} seconds` : ''}`}
			>
				{content}
			</ReasoningCollapse>
		);
	}
);
